<script setup lang="ts">
import SwitchInput from "~/components/Common/Inputs/SwitchInput.vue";
import { useNotificationsStore } from "~/store/notifications/notifications.store";
import GlobalModal from "~/components/Common/Modals/GlobalModal.vue";
import NotificationsMailComposer from "./NotificationsMailComposer.vue";
import { RolesEnums } from "~/utils/enums/roles.enums";
import { ActionTypeEnum, componentAvalaible, typeNotications } from "~/utils/enums/notifications.enums";
import NotificationsTagList, { type IListTag } from "~/components/Common/Notifications/NotificationsTagList.vue";
import Tooltip from "~/components/Common/Tooltip.vue";
import NotificationsEmailTag from "./NotificationsEmailTag.vue";
import { useNotificationsValidation } from "~/composables/notifications/useNotifications";
import { useFeedback } from "~/composables/feedback/useFeedback";
import { useSessionDetailsStore } from "~/store/sessions/session-details-store";
import { TrainingPlanDetailsItemTypeEnum } from "@simbelapp/order-sdk";
import { ItemType } from "~/utils/enums/common.enums";
import LoadButton from "~/components/Common/Buttons/LoadButton.vue";
import { useCompaniesStore } from "~/store/companies/companies.store";
import Collapses from "~/components/Common/Collapses/Collapses.vue";
import type { TrainingPlanIndividual } from "~/utils/interfaces/sessions-interfaces";

const notificationStore = useNotificationsStore();
const sessionDetailsStore = useSessionDetailsStore();
const load = ref(false);
const companiesStore = useCompaniesStore();
const { companyInfos } = storeToRefs(companiesStore);
const selected = ref();
const { notifyModal, type, createSession, actionType } = storeToRefs(notificationStore);
const { sessionDetails } = storeToRefs(sessionDetailsStore);
const mailComposer = ref<InstanceType<typeof NotificationsMailComposer> | null>(null);

const emit = defineEmits(["refreshDocuments", "reset"]);

const props = defineProps({
  elementToShow: {
    type: Array as PropType<componentAvalaible[]>,
  },
  sessionId: {
    type: String,
  },
});

// Unfolded: a chooser asked who the mail goes to before this form opened, so it shows who is addressed.
const isOpen = ref(true);

const validationNotification = useNotificationsValidation();
const $e = await validationNotification.sessionNotifications();

async function send() {
  if (type.value !== typeNotications.session) return;

  if (!$e?.value.$error && hasRecipients.value) {
    if (actionType.value === ActionTypeEnum.SUMMON) {
      await notificationStore.convoked();
    } else {
      await notificationStore.sendNotificationSession();
    }

    load.value = false;

    close();
    return;
  }

  load.value = false;

  // The subject error renders inline under its field, but an empty recipient list had no signal at
  // all: LoadButton runs its « Terminé » animation whatever the parent does, so the admin was told
  // the mail went out when nothing was sent. Reachable now that recipients can be deselected.
  if (!hasRecipients.value) {
    useFeedback().error("Sélectionnez au moins un destinataire", "small");
  }
}

async function close() {
  notifyModal.value = false;
  notificationStore.$reset();

  emit("reset");
}

function refreshDocs() {
  emit("refreshDocuments");
}

// Attendees are camelCase SDK models on every entry point, so reading
// snake_case keys here silently drops the manager. Typed explicitly to catch that drift at build time.
const normalizedAttendees = computed(() =>
  (createSession.value?.attendees ?? []).map((attendee: TrainingPlanIndividual.ISessionDetailsAttendees) => ({
    email: attendee.email ?? null,
    displayValue: attendee.displayValue ?? null,
    managerEmail: attendee.managerEmail ?? null,
    managerDisplayValue: attendee.managerDisplayValue ?? null,
  })),
);

const listAttendees = computed(() =>
  normalizedAttendees.value.filter((e) => e.email).map((e) => ({ email: e.email, displayValue: e.displayValue })),
);

const listAttendeesWithManager = computed(() =>
  normalizedAttendees.value
    .filter((e) => e.email && e.managerEmail)
    .map((e) => ({ email: e.email, displayValue: e.displayValue })),
);

const listAttendeesWithManagerNoUnique = computed(() => normalizedAttendees.value.filter((e) => e.managerEmail));

const listAttendeesManager = computed(() =>
  normalizedAttendees.value
    .filter((e) => e.managerEmail)
    .map((e) => ({ email: e.managerEmail, displayValue: e.managerDisplayValue }))
    .reduce((acc: IListTag[], current) => {
      const exists = acc.find((item) => item.email === current.email);
      return exists ? acc : acc.concat([current]);
    }, []),
);

/** The branch's own recipient list, which is what the send actually needs. */
const hasRecipients = computed(() =>
  notificationStore.isForManagerSession ? !!listAttendeesManager.value.length : !!listAttendees.value.length,
);

const selectedParticipantsCount = computed(() => createSession.value?.attendees?.length ?? 0);

/**
 * Deselect a recipient. Every list here is computed from `createSession.attendees` — the list the
 * send payload is built from — so that is the only thing to write.
 *
 * In the participants branch one chip is one attendee. In the managers branch one chip is one
 * manager ADDRESS, deduped across their team, so dropping it drops every attendee behind it: that is
 * what « do not mail this manager » means, and it keeps the « participant(s) dont le manager sera
 * notifié » list underneath consistent with it.
 */
function removeRecipient(tag: IListTag) {
  const attendees = createSession.value.attendees ?? [];

  createSession.value.attendees = notificationStore.isForManagerSession
    ? attendees.filter((attendee) => attendee.managerEmail !== tag.email)
    : attendees.filter((attendee) => attendee.email !== tag.email);
}

const managersWithoutMailError = computed(() =>
  selectedParticipantsCount.value <= 1
    ? "Le manager du participant sélectionné ne dispose pas d'adresse mail."
    : "Les managers des participants sélectionnés ne disposent pas d'adresse mail.",
);

const managersWithoutMailPrincipalError = computed(() =>
  selectedParticipantsCount.value <= 1
    ? "Un destinataire principal est obligatoire, le manager du participant sélectionné ne dispose pas d'adresse mail."
    : "Un destinataire principal est obligatoire, les managers des participants sélectionnés ne disposent pas d'adresse mail.",
);

const subjectPrefix = computed(() =>
  notificationStore.isForManagerSession ? "{{ Prénom Nom du participant }}" : null,
);

const contentHint = computed(() =>
  notificationStore.isForManagerSession
    ? "Une phrase introductive prévient le manager que la notification concerne un membre de son équipe."
    : null,
);

watch(
  () => createSession.value?.choiceDelivery,
  () => {
    notificationStore.resetSwitchDelivery();
    notificationStore.template(selected.value, props.sessionId);
  },
);

onMounted(async () => {
  await companiesStore.fetchCompanyInfos();
  if (companyInfos.value?.language) {
    selected.value = companyInfos.value.language;
  }
  // Check if session attendees are project users (type === project)
  const isProject = sessionDetails.value?.itemType === ItemType.PROJECT;

  if (isProject && props?.sessionId) {
    await sessionDetailsStore.fetchSessionDetails(props.sessionId, TrainingPlanDetailsItemTypeEnum.Session);
  }
  // The attendees tab's rows come from notify()'s own mail-info fetch and carry their originalId. Refetch
  // when a recipient still has to be resolved: the list left by an earlier flow may be a subset or a notification.
  if (
    !sessionDetailsStore.sessionDetailsAttendeesWithMail?.length ||
    createSession.value.attendees?.some((attendee) => !attendee.originalId)
  ) {
    await sessionDetailsStore.fetchSessionAttendeesWithMail({
      isConvocation: actionType.value === ActionTypeEnum.SUMMON,
    });
  }
  if (sessionDetailsStore.sessionDetails) {
    createSession.value.initDocument.itemId = sessionDetailsStore.sessionDetails.sessionId;
    createSession.value.initDocument.itemType = ItemType.SESSION;
    createSession.value.initDocument.displayValue = "tous";
  }
  notificationStore.notificationEvents();
  if (companyInfos.value?.language) {
    await notificationStore.template(companyInfos.value.language, props.sessionId);
  }

  // After the project → session re-read above: the default template is filled for that session.
  await mailComposer.value?.applyDefaultTemplate();

  if (notificationStore.templateById) {
    notificationStore.createSession.content = notificationStore.templateById.content;
  }
});
</script>

<template>
  <GlobalModal v-if="createSession" style="padding-bottom: 0">
    <template #body>
      <div class="flex flex-col h-full">
        <HeaderDetails back-icon="arrow_left" :close="false" back @backClick="close">
          <template v-if="actionType === ActionTypeEnum.SUMMON" #page>Envoyer des convocations</template>
          <template v-else #page>Envoyer des notifications</template>
        </HeaderDetails>
        <div class="overflow-auto h-full">
          <div class="w-[752px] mx-auto py-[24px]">
            <h3 v-if="actionType === ActionTypeEnum.SUMMON" class="heading_3_demi text-primary-color">
              Envoyer des convocations
            </h3>
            <h3 v-else class="heading_3_demi text-primary-color">Envoyer des notifications</h3>
            <div class="flex flex-col gap-8 p-4">
              <Collapses v-model="isOpen">
                <template #header>
                  <span class="body_regular">Destinataires</span>
                </template>
                <template #content>
                  <div
                    v-if="elementToShow?.includes(componentAvalaible.choiceDelivery)"
                    class="flex flex-col gap-4 mt-4"
                  >
                    <NotificationsTagList
                      v-if="createSession.choiceDelivery === RolesEnums.MANAGER"
                      :list="listAttendeesManager"
                      removable
                      :error="{
                        error: !listAttendeesManager?.length,
                        message: managersWithoutMailPrincipalError,
                      }"
                      @remove="removeRecipient"
                    >
                      <template #header>
                        <div class="flex justify-between w-full mb-2">
                          <div class="flex justify-center items-center gap-1">
                            <span class="body_regular text-secondary-color">Destinataire(s)</span>
                            <Tooltip :position="'top-mid'">
                              <template #content>
                                <div class="flex w-[414px] gap-[8px]">
                                  <img src="/icons/emojis/eyes.svg" alt="" :style="{ height: '16px', width: '16px' }" />
                                  Les managers recevront un mail par participant appartenant à leur équipe.
                                </div>
                              </template>
                            </Tooltip>
                          </div>
                          <!-- <span class="body_regular text-secondary-color"
                        >{{ listAttendeesWithManagerNoUnique.length }}/{{ store.countAttendees }} participant(s) dont le
                        manager sera notifié.</span
                      > -->
                        </div>
                      </template>
                    </NotificationsTagList>
                    <NotificationsTagList
                      v-else
                      :list="listAttendees"
                      removable
                      :error="{ error: !listAttendees?.length, message: '' }"
                      @remove="removeRecipient"
                    >
                      <template #header>
                        <div class="flex justify-between w-full mb-2">
                          <div class="flex justify-center items-center gap-1">
                            <span class="body_regular text-secondary-color">Destinataire(s)</span>
                            <Tooltip :position="'top-mid'">
                              <template #content>
                                <div class="flex w-[414px] gap-[8px]">
                                  <img src="/icons/emojis/eyes.svg" alt="" :style="{ height: '16px', width: '16px' }" />
                                  Chaque participant recevra un mail qui lui sera dédié.
                                </div>
                              </template>
                            </Tooltip>
                          </div>
                          <!-- <span class="body_regular text-secondary-color"
                        >{{ listAttendees.length }}/{{ store.countAttendees }} participant(s) sélectionné(s)
                        notifié(s)</span
                      > -->
                        </div>
                      </template>
                    </NotificationsTagList>
                    <div class="flex flex-col gap-2">
                      <div
                        v-if="createSession.choiceDelivery === RolesEnums.EMPLOYEE"
                        class="flex flex-row gap-1 items-center"
                      >
                        <SwitchInput
                          v-model="createSession.doSendManager"
                          label="Mettre en copie le manager du participant"
                        >
                        </SwitchInput>
                        <Tooltip :position="'top-mid'">
                          <template #content>
                            <div class="flex w-[414px] gap-[8px]">
                              <img src="/icons/emojis/eyes.svg" alt="" :style="{ height: '16px', width: '16px' }" />
                              Les managers en copie reçoivent un seul mail avec la liste des participants dans leur
                              équipe, sans les éventuelles PJ (accessibles sur le détail du participant dans le plan).
                            </div>
                          </template>
                        </Tooltip>
                      </div>
                      <!-- Read-only on purpose, both of them: this one mirrors the recipients above (it lists
                           the participants, not who is mailed), and the one below is driven by the copy-manager
                           switch, which travels as the boolean `copyManagers` with no per-manager list — a cross
                           there would look like it works and change nothing. Removal belongs on Destinataire(s). -->
                      <NotificationsTagList
                        v-if="createSession.choiceDelivery === RolesEnums.MANAGER && listAttendeesWithManager?.length"
                        :list="listAttendeesWithManager"
                      >
                        <template #header>
                          <div class="flex w-full mb-2">
                            <span class="text-secondary-color body_regular"
                              >Participant(s) dont le manager sera notifié :</span
                            >
                          </div>
                        </template>
                      </NotificationsTagList>
                      <NotificationsTagList
                        v-else-if="createSession.doSendManager && listAttendeesManager.length"
                        :list="listAttendeesManager"
                      >
                      </NotificationsTagList>
                      <span
                        v-else-if="createSession.doSendManager && !listAttendeesManager.length"
                        class="body_regular text-primary-red-color"
                      >
                        {{ managersWithoutMailError }}</span
                      >
                    </div>

                    <NotificationsEmailTag v-model="createSession.emailsCopy" multiple searchable add>
                      <template #header>
                        <div class="flex gap-1 items-center">
                          <span class="body_regular text-secondary-color">Autre(s) destinataire(s) en copie</span>
                          <Tooltip :position="'top-mid'">
                            <template #content>
                              <div class="flex w-[414px] gap-[8px]">
                                <img src="/icons/emojis/eyes.svg" alt="" :style="{ height: '16px', width: '16px' }" />
                                Les destinataires en copie recevront un seul mail avec la liste des participants
                                concernés, sans les éventuelles PJ (accessibles sur la page de la session sur Simbel).
                              </div>
                            </template>
                          </Tooltip>
                        </div>
                      </template>
                    </NotificationsEmailTag>
                  </div>
                  <div v-else class="flex flex-col gap-4">
                    <NotificationsEmailTag v-model="createSession.emails" multiple searchable add>
                      <template #header>
                        <div class="flex gap-1 items-center">
                          <span class="text-secondary-color">Destinataire</span>
                        </div>
                      </template>
                    </NotificationsEmailTag>
                    <NotificationsEmailTag v-model="createSession.emailsCopy" multiple searchable add>
                      <template #header>
                        <div class="flex gap-1 items-center">
                          <span class="body_regular text-secondary-color">Autre(s) destinataire(s) en copie</span>
                          <Tooltip :position="'top-mid'">
                            <template #content>
                              <div class="flex w-[414px] gap-[8px]">
                                <img src="/icons/emojis/eyes.svg" alt="" :style="{ height: '16px', width: '16px' }" />
                                Les destinataires en copie recevront un mail groupé pour tous les participants. Ils
                                n'auront pas accès aux documents.
                              </div>
                            </template>
                          </Tooltip>
                        </div>
                      </template>
                    </NotificationsEmailTag>
                  </div>
                </template>
              </Collapses>

              <!-- The picker re-reads the documents on open: this form does not load them up front. -->
              <NotificationsMailComposer
                ref="mailComposer"
                :show-subject="!!elementToShow?.includes(componentAvalaible.subject)"
                :show-content="!!elementToShow?.includes(componentAvalaible.content)"
                :subject-error="$e?.$errors.find((err) => err.$propertyPath === 'subject')"
                :subject-prefix="subjectPrefix"
                :content-hint="contentHint"
                @open-documents="refreshDocs"
                @refresh-documents="refreshDocs"
              >
                <template #options>
                  <SwitchInput
                    v-if="
                      elementToShow?.includes(componentAvalaible.sendIcs) &&
                      createSession.choiceDelivery === RolesEnums.EMPLOYEE
                    "
                    v-model="createSession.metadata.sendIcs"
                    label="Envoyer également au(x) participant(s) une invitation agenda"
                  ></SwitchInput>
                  <SwitchInput
                    v-if="createSession.choiceDelivery === RolesEnums.EMPLOYEE"
                    v-model="createSession.metadata.sendUrlSession"
                    label="Ajouter un lien permettant aux participants d'accéder à la page de la session (incluant détails et documents)."
                  >
                  </SwitchInput>
                </template>
              </NotificationsMailComposer>
            </div>
          </div>
        </div>
        <FooterDetails class="w-[752px] pt-[11px] pb-[12px] mx-auto flex justify-center border-t border-stroke-color">
          <template #page>
            <LoadButton
              v-if="actionType === ActionTypeEnum.SUMMON"
              v-model="load"
              text-confirm="Envoyer les convocations"
              @click="send()"
            ></LoadButton>
            <LoadButton v-else v-model="load" text-confirm="Envoyer les notifications" @click="send()"></LoadButton>
          </template>
        </FooterDetails>
      </div>
    </template>
  </GlobalModal>
</template>

<style scoped lang="scss">
.toggle_button {
  @apply flex items-center justify-center px-[12px];
  box-sizing: border-box;
  height: 32px;
  min-width: 64px;
  color: var(--text-secondary-color);

  &[data-toggled="true"] {
    background: var(--grey-light);
    color: var(--text-main-color);
  }

  &:last-child {
    border-left: solid 2px var(--grey-lightest);
  }
}

.toggle_containers {
  @apply flex flex-row;
  border: solid 2px var(--grey-lightest);
  border-radius: 12px;
  overflow: hidden;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(540deg);
  }
}

.spin {
  animation: spin 0.5s linear infinite;
}
</style>
